import { createAttendance, getAttendanceCount, getAttendanceStudentOne, getAttendanceTotal, getClass, getStudent, getStudentOne, } from "api/api";
import Loading from "components/Loading";
import { NoDataWithImageText } from "components/NoDataComponent";
import { OutlinePrimaryBtn, SolidPrimaryBtn, SolidPrimaryWhiteBtn } from "components/button/Button";
import { AttendanceStatusSelectOption, SelectOption } from "components/input/Input";
import {
  CustomDatePickerWrap,
  EllipsisTextColumn,
  FlexGrow,
  ListContentWrap,
  PageArticleWrap,
  PageHeaderTitleButtonStyle,
} from "components/layout/Layout";
import { getMonth, getYear } from "date-fns";
import React, { createContext, useCallback, useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { ko } from "date-fns/locale";
import styled from "styled-components";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AttendanceInfo,
  AttendanceOneInfo,
  AttendanceStudentAllInfo,
  ClassInfo,
  SingleResult,
  StudentInfo,
  provinceInfo,
  AttendanceRequestType, AttendanceCountResponseType, AttendanceCountQueryParams, AttendanceDayType,
  ClassQueryParams,
  ListResult
} from "type/type";
import {
    AttendanceAdmitAbsentType,
    AttendanceStatusType,
    MONTHS,
    NoStudentDataText,
    NoTeamClassDataText,
    ROLE_DIRECTOR,
    ROLE_TEACHER,
    aesCBCDecrypt,
    changeAttendanceStatus,
    changeDayToString,
    formattedDateHyphenYYYYMMDD,
    formattedHHMM,
    formattedSelectOption,
    formattedSelectOptionNotAll,
    getCurrentYYMMDD,
    ellipsisText,
    getDayClassName,
    userClassSelectOption,
    CURRENTYEAR,
} from "utils/commonUtils";
import { AttendanceAdmitAbsent } from "components/ModalComponent";
import { colors } from "styles/global-styles";
import { RoundIcon } from "components/common/Icon";
import { useLoginUserInfo } from "../../redux/hook/useLoginUserInfo";
import { useRecoilValue } from "recoil";
import { holidayState, selectSiteIdState, userRoleState } from "styles/atoms";
import refreshIcon from "assets/image/common/refresh.svg";

const AttendanceOne = styled.div`
  min-height: 3.75rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({theme}) => theme.newTheme.border.primaryBd};
  > div {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    &:nth-of-type(6) {
      word-break: auto-phrase;
    }
  }
  input[type="text"] {
    background: none;
    border: none;
    width: 100%;
    &:focus,
    &:focus-visible {
      border: none;
    }
  }
  &:hover {
    background-color: ${({theme}) => theme.newTheme.table.tableBodyHoverBg};
  }
  .select-box > div {
    width: 100%;
  }
`;

const MonthlyTable = styled.table`
  border-collapse: collapse;
  border: 0;
  thead {
    position: relative;
    th {
      position: sticky;
      top: 0;
      background-clip: padding-box;
      z-index: 10;
      height: 2.25rem;
      border-bottom: none;
    }
    td {
      position: sticky;
      top: 2.25rem;
      z-index: 10;
      border-top: none;
    }
    tr:first-of-type {
      th {
        border-top: 0;
      }
      .pin {
        z-index: 11;
      }
    }
  }
  .pin {
    z-index: 11;
    &.pin-left {
      left: 0;
      border-left: 0;
      min-width: 100px;
    }
    &.pin-right {
      right: 0;
      border-right: 0;
    }
  }
  th,
  td {
    border: 1px solid ${({theme}) => theme.newTheme.border.primaryBd};
    background-clip: padding-box;
    scroll-snap-align: start;
    padding: 0.5rem;
    min-width: 4.5rem;
    margin: 0;
    font-weight: 400;
  }
  tbody {
    position: relative;
    z-index: 5;
    th:first-of-type,
    tr:nth-of-type(2n) td {
      border-bottom-color: ${({theme}) => theme.newTheme.default.textGray};
    }
    th {
      position: sticky;
    }
    td {
      text-align: left;
    }
  }
  thead th,
  tbody th {
    background-color: ${({theme}) => theme.rootWhite};
  }
  .side-thead {
    td {
      background-color: ${({theme}) => theme.newTheme.table.tableHeadBg};
    }
  }
  .attendance {
    color: ${colors.green};
  }
  .etc {
    color: ${colors.yellow};
  }
  .absent {
    color: ${colors.vermillion};
  }
  .admit-absent {
    color: ${colors.orange};
    white-space: nowrap;
  }
`;

// 출석부 상단 card 부분
export const TwoLineCard = styled.li`
  padding: 0.75rem 1.5rem;
  width: 100%;
  border-radius: 0.5rem;
  background-color: ${colors.lightGray};
  &.total-card {
    background-color: ${colors.lightGray};
  }
  &.status-1 {
    background-color: ${colors.secondaryHover};
  }
  &.check-in-out {
    background-color: ${colors.lightBlue};
  }
  &.status-2 {
    background-color: ${colors.lightYellow};
  }
  &.status-0 {
    background-color: ${colors.deleteBg};
  }
  p:last-of-type {
    font-size: 1.125rem;
    font-family: var(--font-nanumSquare-acB);
  }
`;

let months = MONTHS;

/**
 * StudentAttendance의 Provider
 * - classData, studentData, classSelectOption, attendanceStatus
 */
export const ClassStudentContext = createContext<{
  classData: ClassInfo[];
  studentData: StudentInfo[];
  classSelectOption: provinceInfo[];
  attendanceStatus: provinceInfo[];
}>({ classData: [], studentData: [], classSelectOption: [], attendanceStatus: [] });

// 일별/월별 출석부에 보여주기 위한 Type
type ExtendAttendanceInfo = {
  site_id: number,
  student_id: number,
  student_name: string,
  student_birth_date:string, //yyyy-mm-dd
  class_name: string,
  user_id: number,
  attendance_list: AttendanceOneInfo[]
}
/**
 * 월별 출석부 - 월별 전체 학생 현황 카드
 * @param param0
 * @returns
 */
const MonthlyAttendanceComponent = ({ attendanceList, startDate, classList, studentAllList }
  : { attendanceList: ExtendAttendanceInfo[], startDate: Date; classList: ClassInfo[], studentAllList: StudentInfo[] }):JSX.Element => {

    
  // ************************ props
  // 전역 데이터
  const user_role = useRecoilValue(userRoleState);
  const holidays = useRecoilValue(holidayState);

  const studentCount = studentAllList.length;

  // ************************ state

  const [lastDate, setLastDate] = useState(0); // 월의 말일
  const [days, setDays] = useState<number[]>([]); // 월의 요일 array

  // ************************ handler

  // 더블 클릭 시 일별로 이동 - TODO: 추후에 작업하기
  const navigate = useNavigate();
  function goDailyAttendance(event: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>, { studentId, date }: any) {

    event.preventDefault();
    const classId = classList.find((c: ClassInfo) =>
      c.student_list.find((student: StudentInfo) => studentId === student.id)
    )?.id || 0;
    navigate(`/studentAttendance/daily`, {
      state: {
        oneStudentClass: classId,
        oneStudentId: studentId,
        oneDate: date,
      },
    });
  }

  // ************************ useEffect
  // 월별인 경우에만 요일array 만들기
  useEffect(() => {
    const yyyy = startDate.getFullYear();
    const mm = startDate.getMonth() + 1;
    const lastDay = new Date(yyyy, mm, 0).getDate();
    setLastDate(lastDay);
    setDays([]);
    for (let i = 1; i <= lastDay; i++) {
      const day = new Date(yyyy, mm - 1, i).getDay();
      setDays((prev) => [...prev, day]);
    }
  }, [startDate]);

  return (
    <ListContentWrap style={{height: '100%'}}>
      <div className="list-one-wrap" style={{ height: "100%" }}>
      { // 선생님이면서 담당반이 없는 경우
          user_role === ROLE_TEACHER && !classList.length ? <NoDataWithImageText text={NoTeamClassDataText} />
        : studentCount === 0 ? // 관리자/선생님 학생이 없는 경우
          <NoDataWithImageText text={NoStudentDataText} />
          : <div className="list-table">
              <p className="attendance-table-header">
                {startDate.getMonth() + 1}월 출석부
              </p>
              <div className="primary">
                <MonthlyTable>
                  <thead>
                    <tr>
                      <th className="pin pin-left"></th>
                        {Array.from({ length: lastDate }).map((_, i) => {
                          // 주말/공휴일 체크
                          const pastDate = formattedDateHyphenYYYYMMDD(new Date(startDate.getFullYear(), startDate.getMonth(), i + 1));
                          const isHolidayB = holidays.includes(pastDate);
                          return (
                            <th key={i} className={(days[i] === 0 || isHolidayB) ? 'text-red' : days[i] === 6 ? 'text-blue' : ''}>{i + 1}일</th>
                          )
                        })}
                      <th className="pin pin-right"></th>
                    </tr>
                    <tr className="side-thead">
                      <td className="pin pin-left">이름</td>
                        {days.map((day, index) => {
                          // 주말/공휴일 체크
                          const pastDate = formattedDateHyphenYYYYMMDD(new Date(startDate.getFullYear(), startDate.getMonth(), index + 1));
                          const isHolidayB = holidays.includes(pastDate);
                          return (
                            <td key={index} className={(day === 0 || isHolidayB) ? 'text-red' : day === 6 ? 'text-blue' : ''}>{changeDayToString(day)}</td>
                          )
                        })}
                      <td className="pin pin-right">출결 통계</td>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceList.map((studentAttendance, index) => {
                      const { attendance_list, class_name, student_name } = studentAttendance;
                      let attendanceCount = 0;
                      let absentCount = 0;
                      const _currentDay = new Date();
                      const currentDay = new Date(_currentDay.getFullYear(), _currentDay.getMonth(), _currentDay.getDate());
                      return (
                        <React.Fragment key={`student_${studentAttendance.student_id}`}>
                          <tr key={`attendance_${studentAttendance.student_id}`}>
                            <th className="pin pin-left" rowSpan={2}>
                              <p>{ellipsisText(aesCBCDecrypt(student_name), 6)}</p>
                              <p style={{marginTop: '0.25rem'}}>
                                {class_name && <RoundIcon text={ellipsisText(class_name, 10)} type="primary" />}
                              </p>
                            </th>
                            {attendance_list.map((attendance, i) => {
                              const pastDate_d = new Date(startDate.getFullYear(), startDate.getMonth(), i + 1);
                              const pastDate = formattedDateHyphenYYYYMMDD(new Date(startDate.getFullYear(), startDate.getMonth(), i + 1));
                              const isHolidayB = holidays.includes(pastDate);
                              const status = attendance.status;

                              // 공휴일 체크
                              if (isHolidayB && status === 0) {
                                return (
                                  <td key={`holiday_${studentAttendance.student_id}_${i}`} className="text-red" style={{ textAlign: 'center' }}>
                                    공휴일
                                  </td>
                                )
                              }
                              // 휴일 체크
                              else if (attendance.day_type === "FREEDAY" && currentDay > pastDate_d) {
                                return (
                                  <td key={`freeday_${studentAttendance.student_id}_${i}`} className="text-gray" style={{ textAlign: 'center' }}>
                                    휴일
                                  </td>
                                )
                              }
                              else {
                                // 출석 count - 출결/인정결석/조퇴
                                if (status === 1 || status === 2 || status === 5) attendanceCount += 1;
                                // 결석 count - 결석/병결/사고 등
                                else if (status === 0) { 
                                  if (currentDay > pastDate_d) absentCount += 1;
                                }
                                else absentCount += 1;
                                
                                const checkInTime = attendance.checkin_time || '';
                                return (
                                  status === 0 ?
                                  <td key={`absent_${studentAttendance.student_id}_${i}`} className="absent" style={{ textAlign: 'center' }}>
                                    {currentDay > pastDate_d ? changeAttendanceStatus(status) : "-"}
                                  </td>
                                  : status === 1 ?
                                    <td key={`present_${studentAttendance.student_id}_${i}`} style={{ textAlign: 'center' }}>
                                      {checkInTime ? `${checkInTime.substring(0, 5)}` : "-"}
                                    </td>
                                  :
                                    <td key={`other_${studentAttendance.student_id}_${i}`} className={`${ status === 2 ? "admit-absent" : "etc"}`} style={{ textAlign: 'center' }}>
                                      {changeAttendanceStatus(status)}
                                    </td>
                                );
                              }
                            })}
                            <th className="pin pin-right attendance">
                              출석: {attendanceCount}
                            </th>
                          </tr>
                          <tr key={`checkout_${studentAttendance.student_id}`}>
                            {attendance_list.map((attendance, i) => {
                              const status = attendance.status;
                              const checkOutTime = attendance.checkout_time || '';
                              const pastDate = new Date(startDate.getFullYear(), startDate.getMonth(), i + 1);
                              const pastDateDay = pastDate.getDay();

                              // 주말/공휴일 체크
                              if (status === 0 && (pastDateDay === 0 || pastDateDay === 6)) {
                                return (<td key={`checkout_weekend_${studentAttendance.student_id}_${i}`} style={{ textAlign: 'center' }}></td>)
                              }
                              else {
                                return (
                                  status === 0 ?
                                    <td key={`checkout_absent_${studentAttendance.student_id}_${i}`} className="absent" style={{ textAlign: 'center' }}></td>
                                    : status === 1 ?
                                      <td key={`checkout_present_${studentAttendance.student_id}_${i}`} style={{ textAlign: 'center' }}>
                                        {checkOutTime ? `${checkOutTime.substring(0, 5)}` : "-"}
                                      </td>
                                    : <td key={`checkout_other_${studentAttendance.student_id}_${i}`} style={{ textAlign: 'center' }}></td>
                                );
                              }
                            })}
                            <th className="pin pin-right absent">결석: {absentCount}</th>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </MonthlyTable>
              </div>
          </div>}
      </div>
    </ListContentWrap>
  );
};

/**
 * 일별 출석부 - 일별 전체 학생 출결 상태 카드
 * @param param0
 * @returns
 */
const DailyAttendanceComponent =
  ({ attendanceList, studentAllList, searchClassId, startDate, attendanceStatus, classList, classId, isSuccess, mainApi }
    : {
      attendanceList: ExtendAttendanceInfo[], studentAllList: StudentInfo[], searchClassId: number, startDate: Date,
      attendanceStatus: provinceInfo[], classList: ClassInfo[], classId: number, isSuccess: Function, mainApi: Function
  }): JSX.Element => {
    

  // ************************ props
  // 전역 데이터
  const { access } = useLoginUserInfo();
  const { user_id } = access;

  const user_role = useRecoilValue(userRoleState);
  const selectSiteId = useRecoilValue(selectSiteIdState);

  // 월별에서 더블클릭 했을 때
  const state = useLocation().state as StateProp;
  // console.log("state ", state);

  const start_date = formattedDateHyphenYYYYMMDD(startDate);
  const studentCount = studentAllList.length;
  const defaultInfo: AttendanceOneInfo = {
    id: 0,
    status: 0,
    date: formattedDateHyphenYYYYMMDD(startDate),
    day_type: "",
    is_data: 0,
    reason: '',
    memo: '',
    checkin_time: '',
    checkout_time: '',
    reg_date: "",
    update_date: "",
    user_id: user_id,
    site_id: selectSiteId,
  }
    
  // ************************ state

  const settingOrigin = () => {
    const origin = [...attendanceList.map(list => {
      if (list.attendance_list.length) {
        list.attendance_list = list.attendance_list.filter(a => a.date === start_date);
        return list;
      }
      else return list;
    })];
    return origin;
  };

  const [originAttendanceList, setOriginAttendanceList] = useState<ExtendAttendanceInfo[]>(settingOrigin()); // 수정 시 비교용
  const [updateAttendanceList, setUpdateAttendanceList] = useState<ExtendAttendanceInfo[]>(attendanceList);
  const [checkedItems, setCheckedItems] = useState<boolean[]>(
    new Array(attendanceList.length).fill(false)
  ); // 수정된 출결의 index list(default: 전체 출결 길이만큼의 [])

  const [countData, setCountData] = useState<AttendanceCountResponseType>({
    day_type:AttendanceDayType.WEEKDAY, //출결 날짜 타입
    student_total_count:0, //총 학생 수
    attendance_count:0, //출석 수
    absence_count:0, //결석 수
    approved_absence_count:0, //인정 결석 수
    early_leave_count:0, //조퇴 수
    check_in_count:0, //등교 수
    check_out_count:0, //하교 수
  });

  // 유효성 검사에 실패한 등교/하교 index []
  const [falseOutTimeCheck, setFalseOutTimeCheck] = useState<number[]>([]);

  // ************************ handler

  // 기존 데이터와 수정 데이터 비교 및 체크박스 표시
  function compareData(name: string, value: string | number, index: number) {
    // 기존 데이터와 다른지 체크
    let originData: AttendanceOneInfo;
    if (originAttendanceList[index].attendance_list.length)
      originData = { ...originAttendanceList[index].attendance_list[0] }; // 전달받은 원본값
    else originData = defaultInfo;

    let updateData: AttendanceOneInfo;
    if (updateAttendanceList[index].attendance_list.length)
      updateData = { ...updateAttendanceList[index].attendance_list[0] };
    else updateData = defaultInfo;

    const newCheckedItems = [...checkedItems];

    let diffCnt = 0;

    // 비교할 필드 배열
    const fieldsToCompare = ['status', 'reason', 'checkin_time', 'checkout_time', 'memo'];

    fieldsToCompare.forEach(field => {
      const isModifiedField = field === name;

      // 수정된 필드 비교
      if (isModifiedField) {
        const comparisonValue = field === 'checkin_time' || field === 'checkout_time'
          ? formattedHHMM(String(value)) : value;

        const comparisonOriginValue = field === 'checkin_time' || field === 'checkout_time'
          ? formattedHHMM(originData[field]) : originData[field];

        diffCnt += comparisonOriginValue !== comparisonValue ? 1 : 0;
      }
      // 나머지 필드 비교
      else {
        const comparisonValue = field === 'checkin_time' || field === 'checkout_time'
          ? formattedHHMM(updateData[field]) : updateData[field];

        const comparisonOriginValue = field === 'checkin_time' || field === 'checkout_time'
        ? formattedHHMM(originData[field]) : originData[field];

        diffCnt += comparisonOriginValue !== comparisonValue ? 1 : 0;
      }
    });

    newCheckedItems[index] = diffCnt !== 0;

    setCheckedItems(newCheckedItems);
    setUpdateAttendanceList((prev) => {
      const newData = [...prev];
      let update;
      if (newData[index].attendance_list.length) update = { ...newData[index].attendance_list[0] };
      else update = { ...defaultInfo };

      update[name as keyof AttendanceInfo] = value;
      // 결석/인정결석이 아닌 경우 reason = "" 처리
      if (name === 'status' && !(value === 0 || value === 2)) {
        update.reason = "";
      }
      // 출석/조퇴가 아닌 경우 등교/하교 시간 = "" 처리
      if (name === 'status' && !(value === 1 || value === 5)) {
        update.checkin_time = "";
        update.checkout_time = "";
      }
      else if (name === 'status' && (value === 1 || value === 5)) {
        update.checkin_time = originData.status === 1 && originData.checkin_time ? formattedHHMM(originData.checkin_time) : "";
        update.checkout_time = originData.status === 1 && originData.checkout_time ? formattedHHMM(originData.checkout_time) : "";
      }
      update.student_id = updateAttendanceList[index].student_id;

      newData[index] = {
        ...newData[index],
        attendance_list: [update],
      };
      return newData;
    });
  }

  // 출결상태 변경
  function handleChangeStatus(prop: number, index: number) {
    const update = { ...updateAttendanceList[index].attendance_list[0] };

    // 인정결석인 경우( status = 2 ) 인정결석 선택 modal창 보여주기
    if (prop === 2) {
      setAbsentReason({index, reason: update.reason || ''});
      setIsOpen(true);
    }
    compareData('status', prop, index)
  }

  // 결석사유/비고
  function editAttendanceData(event: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { name, value } = event.target;
    compareData(name, value, index);
  }

  // 등원,하원 시간
  function changeTimeAttendanceData(event: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { name, value } = event.target;
    if (!value) return;

    // 날짜 비교
    // 당일 0, 지난날 -1, 미래 1
    const currentDate = getCurrentYYMMDD();
    const isPastDay = currentDate === start_date ? 0 : currentDate > start_date ? -1 : 1;

    const handleCheckoutTime = (value: string, index: number) => {
      const isPastTime = updateAttendanceList[index].attendance_list[0].checkin_time >= value;

      if (isPastTime) { // 과거 시간
        if (!falseOutTimeCheck.includes(index)) {
          setFalseOutTimeCheck([...falseOutTimeCheck, index]);
        }
      }
      else {
        const newCheck = falseOutTimeCheck.filter((i: number) => i !== index);
        setFalseOutTimeCheck(newCheck);
      }
    }

    if (isPastDay === 0) {
      // 시간 비교 - HH:mm
      const currentTime = new Date().toLocaleTimeString("ko-KR", {
        hour: '2-digit', minute: '2-digit', hour12: false
      });

      // 미래 시간 체크
      if (currentTime < value) {
        return;
      }
      
      // 하교시간인 경우 - 등교 시간 이후인지 체크
      if (name === "checkout_time") {
        handleCheckoutTime(value, index);
      }
      
      compareData(name, value, index);

    }
    else if (isPastDay === -1) {
      // 하교시간인 경우 - 등교 시간 이후인지 체크
      if (name === "checkout_time") {
        handleCheckoutTime(value, index);
      }
        
      compareData(name, value, index);
    }
    else { // 미래 등록 막혀있지만 방어
      alert("미래는 등록할 수 없습니다.");
    }
  }

  // onBlur 핸들러
  function focusOutTimeAttendanceData(event: React.FocusEvent<HTMLInputElement>, index: number) {
    changeTimeAttendanceData(event as unknown as React.ChangeEvent<HTMLInputElement>, index);
  }

  // 취소버튼 클릭
  function editCancel() {
    setUpdateAttendanceList(attendanceList);
    setCheckedItems(new Array(attendanceList.length).fill(false));
  }

  // 출결 등록 및 수정
  async function validateAttendance() {
    try {
      
      //  하교시간 유효성 검사 미통과가 있는 경우
      if (falseOutTimeCheck.length) return;

      // 수정된 출결 기록 리스트 생성
      const requestDataList: AttendanceRequestType[] = checkedItems.reduce((resultList: AttendanceRequestType[],
                                                                            isChecked:boolean,
                                                                            index:number) => {
        //체크된 출결만 리스트에 추가
        if (isChecked) {
          //checkedItems의 index 위치와 updateAttendanceList의 index 위치가 매칭됨
          const attendanceOneInfo:AttendanceOneInfo = updateAttendanceList[index].attendance_list[0];
          resultList.push({
            site_id: selectSiteId,
            student_id: updateAttendanceList[index].student_id,
            user_id: user_id, // 수정하는 사용자 ID (자신의 ID)
            status: attendanceOneInfo.status,
            date: attendanceOneInfo.date,
            reason: attendanceOneInfo.reason,
            checkin_time: attendanceOneInfo.checkin_time,
            checkout_time: attendanceOneInfo.checkout_time,
            memo: attendanceOneInfo.memo,
          });
        }
        return resultList;
      }, []);

      // 요청 보내기 (POST 또는 UPDATE)
      const promises = requestDataList.map((reqData) => {
        // 기존 출석이 있어도 POST로 보내기 (서버에서 알아서 처리됨)
        return createAttendance(reqData);
      });

      const results = await Promise.allSettled(promises);

      // 성공과 실패 결과 처리
      const successCnt = results.filter(res => res.status === 'fulfilled' && res.value.code === 200).length;
      const erroRes = results.filter(res => res.status === 'rejected' || (res.status === 'fulfilled' && res.value.code !== 200));

      // 결과에 따른 알림 표시
      if (requestDataList.length === successCnt) {
        alert(successCnt === 1 ? '출결 등록 및 수정되었습니다.' : '전체 등록 및 수정되었습니다.');
      }
      else if (successCnt === 0) {
        alert('전체 등록 및 수정이 실패됐습니다.');
        console.error(`Error in Promise `, erroRes);
      }
      else {
        alert('일부만 등록 및 수정되었습니다.');
        console.error(`부분 Error in Promise `, erroRes);
      }

      // 성공 여부 설정 및 체크된 아이템 초기화
      isSuccess(true);
      setCheckedItems(new Array(attendanceList.length).fill(false));

      // 데이터 갱신
      mainApi();
    } catch (error) {
      console.error("updateAttendance Promise error:", error);
    }
    finally {
    }
  }

  // ************************ 인정결석 모달
  const [absentReason, setAbsentReason] = useState<{ index: number, reason: string }>({ index: 0, reason: '' });
  const [isOpen, setIsOpen] = useState(false);
  // 모달창 닫았는지 확인
  function closeModal(prop: boolean) {
    setIsOpen(prop);
  }

  function responseData(index: number, reason: string) {
    const reasonValue = AttendanceAdmitAbsentType.find(type => type.value === reason);

    setUpdateAttendanceList((prev) => {
      const newData = [...prev];
      let update;
      if (newData[index].attendance_list.length) update = { ...newData[index].attendance_list[0] };
      else update = { ...defaultInfo };
      update.reason = reasonValue?.label || reason;

      newData[index] = {
        ...newData[index],
        attendance_list: [update],
      };
      return newData;
    });
    setIsOpen(false);
  }

  async function attendanceCountHandler(){

    //파라미터 정의
    const queryParams:AttendanceCountQueryParams = {
        class_id: searchClassId,
        date: start_date,
    }

    //출결 통계 카운트 조회
    const res:SingleResult<AttendanceCountResponseType> = await getAttendanceCount(queryParams);
    if(res.code === 200){
      const result:AttendanceCountResponseType = res.data;
      setCountData(result);
    }

  }

  // ************************ useEffect

  useEffect(() => {
    if (user_role === ROLE_TEACHER && classId === -1) return;
    attendanceCountHandler();
  }, []);

  // 날짜나 attendanceList가 변경될 때마다 데이터 초기화
  useEffect(() => {
    const originList = settingOrigin();
    setOriginAttendanceList(originList);
    setUpdateAttendanceList(originList);
    setCheckedItems(new Array(originList.length).fill(false));

  }, [attendanceList, start_date]);

  return (
    <>
      <ul className="attendance-list-card-wrap">
        <TwoLineCard className="total-card">
          <p>전체 학생</p>
          <p>{countData.student_total_count}명</p>
        </TwoLineCard>
        <TwoLineCard className="status-1">
          <p>
            <span>출석</span>
            <span style={{fontSize:'0.8rem'}}> (하교)</span>
          </p>
          <p>
            <span>
              {countData.attendance_count}명
            </span>
            <span style={{fontSize:'0.8rem'}}> ({countData.check_out_count}명)</span>
          </p>
        </TwoLineCard>
        <TwoLineCard className="status-0">
          <p>결석</p>
          <p>{countData.absence_count}명</p>
        </TwoLineCard>
        <TwoLineCard className="check-in-out">
          <p>조퇴</p>
          <p>
            {countData.early_leave_count}명
          </p>
        </TwoLineCard>
        <TwoLineCard className="status-2">
          <p>인정결석</p>
          <p>{countData.approved_absence_count}명</p>
        </TwoLineCard>
      </ul>
      <ListContentWrap>
        <div className="list-one-wrap" style={{ height: "calc(100% - 1rem)" }}>
        { // 선생님이면서 담당반이 없는 경우
          user_role === ROLE_TEACHER && !classList.length ? <NoDataWithImageText text={NoTeamClassDataText} />
          : studentCount === 0 ? // 관리자/선생님 학생이 없는 경우
            <NoDataWithImageText text={NoStudentDataText} />
          : <div className="list-table">
              <div>
                <div className="button-wrap">
                  <SolidPrimaryWhiteBtn className="default-btn" onClick={editCancel}>취소</SolidPrimaryWhiteBtn>
                  <SolidPrimaryBtn className="default-btn" onClick={validateAttendance}>완료</SolidPrimaryBtn>
                </div>
                <nav className="list-table-header">
                  <div className="w-5"></div>
                  <div className="w-5">번호</div>
                  <FlexGrow className="w-10">반 이름</FlexGrow>
                  <FlexGrow className="w-10">학생 이름</FlexGrow>
                  <div className="w-15">출결 상태</div>
                  <div className="w-15">결석 사유</div>
                  <div className="w-15">등교 시간</div>
                  <div className="w-15">하교 시간</div>
                  <FlexGrow className="w-10">비고</FlexGrow>
                </nav>
                {updateAttendanceList.map((attendanceOne: ExtendAttendanceInfo, index) => (
                  <AttendanceOne key={`student_${attendanceOne.student_id}`}>
                    <div className="w-5">
                      <input type="checkbox" checked={checkedItems[index]} readOnly />
                    </div>
                    <div className="w-5">
                      <p>{index + 1}</p>
                    </div>
                    <FlexGrow className="w-10">
                      <EllipsisTextColumn>{attendanceOne.class_name || "-"}</EllipsisTextColumn>
                    </FlexGrow>
                    <FlexGrow className="w-10">
                      <EllipsisTextColumn>{aesCBCDecrypt(attendanceOne.student_name)}</EllipsisTextColumn>
                    </FlexGrow>
                    {Array(attendanceOne.attendance_list[0]).map((a, i) => {
                      const one = a ? a : defaultInfo;
                      return (
                        <React.Fragment key={`attendance_${attendanceOne.student_id}_${index}`}>
                          <div className="w-15 select-box">
                            <AttendanceStatusSelectOption
                              key={`${one.id}_status`}
                              optionData={attendanceStatus}
                              defaultValue={one.status}
                              onChange={(prop: number) => handleChangeStatus(prop, index)}
                            />
                          </div>
                          <div className="w-15">
                            <input
                              key={`${one.id}_reason`}
                              type="text"
                              name="reason"
                              value={one.reason}
                              disabled={!(one.status === 0 || one.status === 2)}
                              onChange={(event) => editAttendanceData(event, index)}
                              placeholder="사유 입력"
                            />
                          </div>
                          <div className="w-15">
                            <input
                              key={`${one.id}_checkin`}
                              type="time"
                              name="checkin_time"
                              disabled={!(one.status === 1 || one.status === 5)}
                              value={one.checkin_time ? formattedHHMM(one.checkin_time) : ''}
                              onChange={(event) => changeTimeAttendanceData(event, index)}
                              onBlur={(event) => focusOutTimeAttendanceData(event, index)}
                            />
                          </div>
                          <div className="w-15">
                            <input
                              key={`${one.id}_checkout`}
                              type="time"
                              name="checkout_time"
                              disabled={!((one.status === 1 || one.status === 5) && originAttendanceList[index].attendance_list[0].checkin_time)}
                              value={one.checkout_time ? formattedHHMM(one.checkout_time) : ''}
                              onChange={(event) => changeTimeAttendanceData(event, index)}
                              onBlur={(event) => focusOutTimeAttendanceData(event, index)}
                              className={falseOutTimeCheck.includes(index) ? "time-false" : ""}
                            />
                          </div>
                          <div className="w-10">
                            <input
                              key={`${one.id}_memo`}
                              type="text"
                              name="memo"
                              value={one.memo || ""}
                              onChange={(event) => editAttendanceData(event, index)}
                              placeholder="내용 입력"
                            />
                          </div>
                        </React.Fragment>
                      )}
                    )}
                  </AttendanceOne>
                ))}
              </div>
            </div>
          }
        </div>
      </ListContentWrap>
      {isOpen && (
        <AttendanceAdmitAbsent
          isOpen={isOpen}
          content={absentReason}
          isResponse={responseData}
          isClose={closeModal}
        />
      )}
    </>
  );
};

type StateProp = {
  oneStudentClass: number;
  oneStudentId: number;
  oneDate: string;
};
export function StudentAttendanceList(): JSX.Element {
  // ************************ props

  const { type } = useParams();
  // 전역 데이터
  const { access, user } = useLoginUserInfo();
  const { user_id } = access;
  const user_role = useRecoilValue(userRoleState);
  const selectSiteId = useRecoilValue(selectSiteIdState);

  const holidayList = useRecoilValue(holidayState);
  
  // ************************ state

  const [loading, setLoading] = useState(true);

  const [classList, setClassList] = useState<ClassInfo[]>([]);
  const [classId, setClassId] = useState(-1);
  const [classSelectOption, setClassSelectOption] = useState<provinceInfo[]>([]);

  const [studentAllList, setStudentAllList] = useState<StudentInfo[]>([]);

  // 출결 상태
  const attendanceStatus: provinceInfo[] = formattedSelectOptionNotAll(AttendanceStatusType);
  
  // 상단 필터 - 반/학생/조회 날짜
  const [searchObj, setSearchObj] = useState({
    class_id: user_role === ROLE_DIRECTOR ? 0 : classId,
    student_id: 0,
    start_date: type === "daily" ? new Date() : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    end_date: type === "daily" ? new Date() : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
  });

  // 상단 필터 - 학생 select-option
  const [studentSelectOption, setStudentSelectOption] = useState<provinceInfo[]>([{ label: "전체", value: 0 }]);
  // 상단 필터 - 조회 날짜
  const [startDate, setStartDate] = useState<Date>(
    type === "daily" ? new Date() : new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  // 해당 월 출석기록
  const [attendanceListOnMonth, setAttendanceListOnMonth] = useState<ExtendAttendanceInfo[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);

  // ************************ handler

  // 반 선택
  function handleClassChangeOption(prop: number) {
    // 해당 반 학생 데이터 select-option에 보내기
    setSearchObj((prev) => ({ ...prev, class_id: prop, student_id: 0}));

    let classStudents:StudentInfo[] = [];
    if (prop) {
      const selectClass = classList.find((c: ClassInfo) => c.id === prop);
      classStudents = selectClass ? selectClass.student_list : [];
    }

    const studentData = classStudents.length ?
      formattedSelectOption(classStudents.map((s: StudentInfo) => ({ label: aesCBCDecrypt(s.name), value: s.id }))) // 학생리스트가 있음
      : []; // 학생 리스트가 없음
    setStudentSelectOption(studentData);
  }
  // 학생 선택
  function handleStudentChangeOption(prop: number) {
    setSearchObj((prev) => ({ ...prev, student_id: prop }));
  }

  // 조회 날짜
  function handleChangeSearchDate(date: Date) {
    setStartDate(date);
    // 일간: start_date = end_date, 월간: start_date = 1, end_date = 28/29/30/31
    const start = type === 'daily' ? date : new Date(date.getFullYear(), date.getMonth(), 1);
    const end = type === 'daily' ? date : new Date(date.getFullYear(), date.getMonth() + 1, 0);
    setSearchObj((prev) => ({
      ...prev,
      start_date: start,
      end_date: end,
    }));

    // 출결내용 초기화
    setAttendanceListOnMonth([]);
    setLoading(true);
  }

  // 필터 초기화
  function resetFilter() {
    // 검색 필터 초기화
    setSearchObj({
      class_id: classId,
      student_id: 0,
      start_date: type === "daily" ? new Date() : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      end_date: type === "daily" ? new Date() : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
    });
    // setStudentSelectOption([]);
    // 달력 초기화
    setStartDate(type === "daily" ? new Date() : new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  }

  /**
   * - mainAPi - 월별 출석 기록 가져오기(일별은 월별 기록에서 해당 일자만 filter 걸어서 보여주기)
   * - 반 전체 조회: class_id만 전달
   * - 학생 한 명 조회: student_id만 전달
   */
  const mainApi = useCallback(async () => {
    try {

      setLoading(true);

      // 학생 개별 조회 시에는 class_id 전달x
      const { class_id, student_id } = searchObj;
      const start_date = formattedDateHyphenYYYYMMDD(new Date(searchObj.start_date.getFullYear(), searchObj.start_date.getMonth(), 1));
      const end_date = formattedDateHyphenYYYYMMDD(new Date(searchObj.end_date.getFullYear(), searchObj.end_date.getMonth() + 1, 0));

      const searchOne = ((class_id === 0 && student_id === 0) || (class_id !== 0 && student_id === 0)) ? false : true;
      // searchOne = true면 한명 조회, student_id + 날짜 전달 = API 개별
      // searchOne = false면 반 조회, class_id + 날짜 전달 = getAttendanceTotal
      const prop = searchOne ? { start_date, end_date } : { class_id, start_date, end_date };

      const attendanceRes = searchOne ? await getAttendanceStudentOne(student_id, prop) : await getAttendanceTotal(prop);

      if (attendanceRes.code === 200) {

        let studentAttendances: AttendanceStudentAllInfo[] = Array.isArray(attendanceRes.data) ? attendanceRes.data : [attendanceRes.data];

        if (!Array.isArray(studentAttendances)) studentAttendances = [studentAttendances];

        const dataByStudent: ExtendAttendanceInfo[] = studentAttendances.map(attendance => ({
          site_id: selectSiteId,
          student_id: attendance.student_id,
          student_name: attendance.student_name,
          class_name: attendance.class_name,
          student_birth_date: attendance.student_birth_date,
          user_id: user.id,
          attendance_list: attendance.attendance_list
        }));
        setAttendanceListOnMonth(dataByStudent);
      }
      else {
        setAttendanceListOnMonth([]);
        alert(attendanceRes.message);
      }

    } catch (error) {
      console.error("getAttendance API ERROR ", error);
      setAttendanceListOnMonth([]);
    }
    finally {
      setLoading(false);
    }
  }, [searchObj, user.id, selectSiteId]);

  /**
   * 반 목록 조회하기
   */
  const getClassList = useCallback(async () => {
    try {
      
      // 반 목록 가져오기
      const classQueryParams: ClassQueryParams = {
        name: "",
        user_id: user_role === ROLE_DIRECTOR ? 0 : user_id,
        response_format: "",
        page: 1,
        size: 99999999
      };
  
      const classResult: ListResult<ClassInfo> = await getClass(classQueryParams);
  
      // 반 정보가 없는 경우
      if (classResult.code !== 200 || (classResult.code === 200 && classResult.data.length === 0)) {
        setClassList([]);
      }
      else {
        const userClassList: ClassInfo[] = classResult.data;
        setClassList(userClassList);

        const option = userClassSelectOption(userClassList, user_role);
        setClassSelectOption(option);
        
        const _classId = user_role === ROLE_DIRECTOR ? 0 : userClassList.length ? userClassList[0].id : -1;
        setClassId(_classId);
        setSearchObj(prev => ({ ...prev, class_id: _classId }));
      }

    } catch (error) {
      console.error("api error ", error);
    }
    
  }, [user_id, user_role]);

  // ************************ useEffect
  
  // 선생님 + 담당반이 없을 때
  useEffect(() => {
    if (user_role === ROLE_TEACHER && classId === -1) {
      setAttendanceListOnMonth([]);
      setLoading(false);
    }
  }, [user_role, classId]);

  useEffect(() => {
    const updateStudentList = async () => {
      // 기존) 관리자 - 전체 학생 불러오기(반이 등록 안된 학생도 있을 수 있음)
      if (user_role === ROLE_DIRECTOR) {
        // 반: 전체 / 학생: 전체
        if (searchObj.class_id === 0 && searchObj.student_id === 0) {
          const studentResult = await getStudent();
          setStudentAllList(studentResult.code === 200 ? studentResult.data : []);
        }
        // 반: 전체 / 학생: 선택
        else if (searchObj.class_id === 0 && searchObj.student_id) {
          const studentOneRes = await getStudentOne(searchObj.student_id);
          setStudentAllList(studentOneRes.code === 200 ? studentOneRes.data : []);
        }
      }
      else if (user_role === ROLE_TEACHER && classList.length) { // 선생님인 경우
        setStudentAllList(classList.flatMap((c: ClassInfo) => c.student_list));
      }
      else setStudentAllList([]);
    }

    updateStudentList();
  }, [user_role, classList, searchObj.class_id, searchObj.student_id]);
  
  useEffect(() => {
    getClassList();
  }, [getClassList]);

  useEffect(() => {
    mainApi();
  }, [mainApi, type, isSuccess]);

  useEffect(() => {
    if (studentAllList.length) {
      setStudentSelectOption(formattedSelectOption(studentAllList.map((s: StudentInfo) => ({label: aesCBCDecrypt(s.name), value: s.id}))));
    }
  }, [studentAllList]);

  return (
    <>
      <PageHeaderTitleButtonStyle>
        <p className="header-title">
          {type === "daily" ? "일간 출석부" : "월간 출석부"}
        </p>
      </PageHeaderTitleButtonStyle>
      <PageArticleWrap>
        <nav className="filter-container">
          <div className="filter-wrap">
            <label>반</label>
            <SelectOption
              optionData={classSelectOption}
              defaultValue={searchObj.class_id}
              onChange={(prop: number) => handleClassChangeOption(prop)}
              dontChange={user_role !== ROLE_DIRECTOR}
              placeholderText={!classSelectOption.length && user_role === ROLE_DIRECTOR ? "등록된 반 없음" : !classSelectOption.length ? "담당반 없음" : ""}
            />
          </div>
          <div className="filter-wrap">
            <label>학생</label>
            {!classList.length && user_role === ROLE_TEACHER ? // 선생님 - 담당반이 없는 경우
              <input value={"담당반을 먼저 지정해주세요."} disabled style={{ height: 'auto' }} />
              :
              searchObj.class_id && !studentSelectOption.length ? // 등록된 반은 있지만 해당 반에 학생이 없는 경우
                <input value={"등록된 학생이 없어요."} disabled style={{ height: 'auto' }} />
                :
                <SelectOption
                  optionData={studentSelectOption}
                  defaultValue={searchObj.student_id}
                  onChange={(prop: number) =>
                    handleStudentChangeOption(prop)
                  }
                />
            }
          </div>
          <CustomDatePickerWrap className="custom-date-picker">
            <label>날짜</label>
            {type === "daily" ? (
              <ReactDatePicker
                enableTabLoop={false}
                locale={ko}
                dateFormat="yyyy-MM-dd (E)" // 날짜형태
                formatWeekDay={(day) => day.substring(0, 1)}
                shouldCloseOnSelect // 날짜 선택 시 datepicker 자동 닫힘
                todayButton="오늘"
                selected={startDate}
                onChange={(date) => handleChangeSearchDate(date || new Date())}
                dropdownMode="select"
                minDate={new Date(`${CURRENTYEAR - 3}-01-01`)}
                maxDate={new Date()}
                dayClassName={(d) => getDayClassName(d, startDate, holidayList)}
                renderCustomHeader={({
                  date,
                  changeMonth,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => (
                  <div className="custom-header-container">
                    <div>
                      <button type="button" onClick={decreaseMonth} className="custom-month-button" disabled={prevMonthButtonDisabled}>
                        <img src="/image/icon/arrow_left.svg" alt="left" />
                      </button>
                      <span className="custom-year">{getYear(date)}</span>
                      <select value={months[getMonth(date)]} className="custom-month"
                        onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}>
                        {months.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <button type="button" onClick={increaseMonth} className="custom-month-button" disabled={nextMonthButtonDisabled}>
                        <img src="/image/icon/arrow_right.svg" alt="right" />
                      </button>
                    </div>
                  </div>
                )}
              />
            ) : (
              <ReactDatePicker
                enableTabLoop={false}
                locale={ko}
                dateFormat="yyyy-MM" // 날짜형태
                showMonthYearPicker
                shouldCloseOnSelect // 날짜 선택 시 datepicker 자동 닫힘
                todayButton="이번달"
                showFourColumnMonthYearPicker
                selected={startDate}
                onChange={(date) => handleChangeSearchDate(date || new Date())}
                minDate={new Date(`${CURRENTYEAR - 3}-01-01`)}
                maxDate={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)}
                monthClassName={(d) => d.getMonth() === startDate.getMonth() ? "selectedMonth" : "unSelectedMonth"}
                renderCustomHeader={({
                  date,
                  decreaseYear,
                  increaseYear,
                  prevYearButtonDisabled,
                  nextYearButtonDisabled,
                }) => (
                  <div className="custom-header-container">
                    <div>
                      <button type="button" onClick={decreaseYear} className="custom-month-button" disabled={prevYearButtonDisabled}>
                        <img src="/image/icon/arrow_left.svg" alt="left" />
                      </button>
                      <span className="custom-year">{getYear(date)}</span>
                      <button type="button" onClick={increaseYear} className="custom-month-button" disabled={nextYearButtonDisabled}>
                        <img src="/image/icon/arrow_right.svg" alt="right" />
                      </button>
                    </div>
                  </div>
                )}
              />
            )}
          </CustomDatePickerWrap>
          <OutlinePrimaryBtn type="button" className="little-btn contain-icon" onClick={resetFilter}>
            <img srcSet={refreshIcon} alt="refresh"/>
          </OutlinePrimaryBtn>
          {/* <SolidPrimarySubBtn className="default-btn" onClick={clickSearchMonthly}>조회</SolidPrimarySubBtn> */}
        </nav>
        <article className="list-content-container">
          {loading ? <Loading /> :
            type === "daily" ?
              <DailyAttendanceComponent
                attendanceList={attendanceListOnMonth}
                studentAllList={studentAllList}
                searchClassId={searchObj.class_id}
                startDate={searchObj.start_date}
                attendanceStatus={attendanceStatus}
                classList={classList}
                classId={classId}
                isSuccess={setIsSuccess}
                mainApi={mainApi}
              />
            : <MonthlyAttendanceComponent
                attendanceList={attendanceListOnMonth}
                studentAllList={studentAllList}
                classList={classList}
                startDate={searchObj.start_date}
                />
          }
        </article>
      </PageArticleWrap>
    </>
  );
}

export function StudentAttendance(): JSX.Element {
  return (
    <><Outlet /></>
  );
}